/**
 * /me/loans/new — Employee Request Loan Wizard (M3X.LOANS PR5 — F.3).
 *
 * Multi-section wizard matching the PeopleHub ESS pattern with the
 * agentic re-frame: every step shows what each agent thinks in real
 * time. Sections:
 *
 *   1. Loan Request Information — type picker (loan_product_id)
 *   2. Amount & Tenure — live Mutaba'aa Article 90 check + Tashghil
 *      tenure-extension suggestion when cap is close
 *   3. Supporting Documents — drag-drop upload (placeholder for v1)
 *   4. Purpose — free text + Solvait AI ✦ assist button (PR23 wires
 *      the live LLM call; v1 stub for now)
 *
 * Footer: Save as Draft + Submit.
 *
 * Per pack §4.2. Submit posts to /api/loans/applications and creates
 * a `submitted` loan application. The Concierge intent (M16.8) already
 * has a similar flow via WhatsApp; this is the web-form equivalent.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { Article90HeadroomWidget } from "../components/Article90HeadroomWidget";
import { api, type LoanProductDetail } from "../lib/api";
import { useLegalEntityId } from "../lib/legalEntity";
import { ErrorPanel, Loading } from "../components/Loading";
import { PageHeader } from "../components/ui/PageHeader";

type Section = "type" | "amount" | "docs" | "purpose" | "review";

const SECTIONS: { id: Section; labelKey: string; defaultLabel: string }[] =
  [
    { id: "type", labelKey: "wiz_loan.type", defaultLabel: "Loan Type" },
    {
      id: "amount",
      labelKey: "wiz_loan.amount",
      defaultLabel: "Amount & Tenure",
    },
    { id: "docs", labelKey: "wiz_loan.docs", defaultLabel: "Documents" },
    {
      id: "purpose",
      labelKey: "wiz_loan.purpose",
      defaultLabel: "Purpose",
    },
    {
      id: "review",
      labelKey: "wiz_loan.review",
      defaultLabel: "Review & Submit",
    },
  ];

interface DraftState {
  loan_product_id: string;
  requested_amount: string;
  requested_tenure_months: number;
  purpose: string;
}

const EMPTY_DRAFT: DraftState = {
  loan_product_id: "",
  requested_amount: "",
  requested_tenure_months: 12,
  purpose: "",
};

const LOCAL_STORAGE_KEY = "m3x_loans_pr5_draft";

function loadDraft(): DraftState {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (raw) {
      return { ...EMPTY_DRAFT, ...(JSON.parse(raw) as Partial<DraftState>) };
    }
  } catch {
    /* swallow */
  }
  return EMPTY_DRAFT;
}

function saveDraft(d: DraftState): void {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(d));
  } catch {
    /* swallow */
  }
}

function clearDraft(): void {
  try {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  } catch {
    /* swallow */
  }
}

export function RequestLoanWizardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const employeeId = search.get("employee_id") ?? "";
  const legalEntityId = useLegalEntityId() ?? "";

  const [section, setSection] = useState<Section>("type");
  const [draft, setDraft] = useState<DraftState>(() => loadDraft());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const productsQuery = useQuery({
    queryKey: ["my-loans-products", legalEntityId],
    queryFn: () => api.listLoanProducts(legalEntityId, false),
    enabled: Boolean(legalEntityId),
  });

  const selectedProduct = useMemo<LoanProductDetail | undefined>(
    () =>
      productsQuery.data?.find((p) => p.id === draft.loan_product_id),
    [productsQuery.data, draft.loan_product_id],
  );

  const updateDraft = (patch: Partial<DraftState>): void => {
    const next = { ...draft, ...patch };
    setDraft(next);
    saveDraft(next);
  };

  const progressPct = useMemo(() => {
    let done = 0;
    if (draft.loan_product_id) done += 25;
    if (
      draft.requested_amount &&
      Number.parseFloat(draft.requested_amount) > 0
    )
      done += 25;
    if (draft.requested_tenure_months > 0) done += 25;
    if (draft.purpose.trim().length > 0) done += 25;
    return done;
  }, [draft]);

  const canSubmit =
    draft.loan_product_id !== "" &&
    Number.parseFloat(draft.requested_amount || "0") > 0 &&
    draft.requested_tenure_months > 0;

  const handleSubmit = async () => {
    if (!canSubmit || !employeeId || !legalEntityId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.createLoanApplication({
        employee_id: employeeId,
        legal_entity_id: legalEntityId,
        loan_product_id: draft.loan_product_id,
        requested_amount: draft.requested_amount,
        requested_tenure_months: draft.requested_tenure_months,
        purpose: draft.purpose || null,
        currency: "SAR",
      });
      clearDraft();
      navigate(
        `/me/payment-hub?tab=loans&employee_id=${encodeURIComponent(employeeId)}`,
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!employeeId || !legalEntityId) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
        {t(
          "wiz_loan.missing_params",
          "Sign in to start a loan request. HR can start one for any employee from the Employees page.",
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <PageHeader
        title={t("wiz_loan.page_title", "Request a Loan")}
        subtitle={t(
          "wiz_loan.page_subtitle",
          "Tashghil checks eligibility live; Mutaba'aa shows your Article 90 headroom.",
        )}
      />

      <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-medium text-text-tertiary uppercase">
            {t("wiz_loan.progress", "Form completion")}
          </div>
          <div className="text-xs font-mono">{progressPct}%</div>
        </div>
        <div className="h-2 bg-white border border-slate-300 rounded-full overflow-hidden">
          <div
            className="h-full bg-brand-blue"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      <nav
        role="tablist"
        aria-label={t("wiz_loan.steps_label", "Request loan steps")}
        className="flex gap-1 border-b border-slate-200 overflow-x-auto"
      >
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            role="tab"
            aria-selected={section === s.id}
            onClick={() => setSection(s.id)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px whitespace-nowrap ${
              section === s.id
                ? "border-brand-blue text-brand-deep font-semibold"
                : "border-transparent text-text-secondary"
            }`}
          >
            {t(s.labelKey, s.defaultLabel)}
          </button>
        ))}
      </nav>

      <div className="rounded-lg border border-slate-200 bg-surface-default p-6">
        {section === "type" ? (
          <TypeSection
            products={productsQuery.data ?? []}
            loadingProducts={productsQuery.isLoading}
            errorProducts={productsQuery.error}
            selectedId={draft.loan_product_id}
            onSelect={(id) => updateDraft({ loan_product_id: id })}
          />
        ) : null}
        {section === "amount" ? (
          <AmountSection
            employeeId={employeeId}
            product={selectedProduct}
            requestedAmount={draft.requested_amount}
            requestedTenure={draft.requested_tenure_months}
            onAmountChange={(v) => updateDraft({ requested_amount: v })}
            onTenureChange={(v) =>
              updateDraft({ requested_tenure_months: v })
            }
          />
        ) : null}
        {section === "docs" ? <DocsSection /> : null}
        {section === "purpose" ? (
          <PurposeSection
            purpose={draft.purpose}
            productCode={selectedProduct?.code}
            onChange={(v) => updateDraft({ purpose: v })}
          />
        ) : null}
        {section === "review" ? (
          <ReviewSection
            draft={draft}
            employeeId={employeeId}
            product={selectedProduct}
            canSubmit={canSubmit}
            submitting={submitting}
            onSubmit={handleSubmit}
            submitError={submitError}
          />
        ) : null}
      </div>

      <div className="flex items-center justify-between text-sm">
        <button
          onClick={() => {
            clearDraft();
            setDraft(EMPTY_DRAFT);
          }}
          className="text-text-tertiary hover:text-text-primary"
        >
          {t("wiz_loan.clear_draft", "Clear draft")}
        </button>
        <span className="text-xs text-text-tertiary">
          {t(
            "wiz_loan.autosave_note",
            "Draft auto-saved to this browser. Submit when ready.",
          )}
        </span>
      </div>
    </div>
  );
}

function TypeSection({
  products,
  loadingProducts,
  errorProducts,
  selectedId,
  onSelect,
}: {
  products: LoanProductDetail[];
  loadingProducts: boolean;
  errorProducts: unknown;
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation();
  if (loadingProducts) return <Loading />;
  if (errorProducts) {
    return (
      <ErrorPanel
        message={t(
          "wiz_loan.load_products_error",
          "Couldn't load loan products.",
        )}
      />
    );
  }
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">
        {t("wiz_loan.type_title", "Which type of loan?")}
      </h2>
      <p className="text-sm text-text-secondary">
        {t(
          "wiz_loan.type_subtitle",
          "Tashghil pre-checks eligibility on your selection.",
        )}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {products.map((p) => (
          <label
            key={p.id}
            className={`block rounded-md border p-3 cursor-pointer transition-colors ${
              selectedId === p.id
                ? "border-brand-blue ring-2 ring-brand-blue/30 bg-brand-blue/5"
                : "border-slate-200 hover:border-slate-300"
            }`}
          >
            <input
              type="radio"
              name="loan_product"
              value={p.id}
              checked={selectedId === p.id}
              onChange={() => onSelect(p.id)}
              className="sr-only"
            />
            <div className="font-medium">{p.name_en}</div>
            <div className="text-xs text-text-tertiary">
              {p.name_ar} · {p.code}
            </div>
            <div className="mt-2 text-xs text-text-secondary">
              {t("wiz_loan.max_amount", "Max")}:{" "}
              <span className="font-mono">{p.max_amount} SAR</span>
              {" · "}
              {t("wiz_loan.max_tenure", "Tenure")}:{" "}
              <span className="font-mono">
                {p.max_tenure_months} mo
              </span>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

function AmountSection({
  employeeId,
  product,
  requestedAmount,
  requestedTenure,
  onAmountChange,
  onTenureChange,
}: {
  employeeId: string;
  product: LoanProductDetail | undefined;
  requestedAmount: string;
  requestedTenure: number;
  onAmountChange: (v: string) => void;
  onTenureChange: (v: number) => void;
}) {
  const { t } = useTranslation();
  if (!product) {
    return (
      <p className="text-sm text-text-secondary">
        {t("wiz_loan.pick_type_first", "Pick a loan type first.")}
      </p>
    );
  }
  const maxAmount = Number.parseFloat(product.max_amount);
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">
        {t("wiz_loan.amount_title", "How much and over how long?")}
      </h2>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-text-tertiary mb-1">
            {t("wiz_loan.requested_amount", "Amount (SAR)")}
          </label>
          <input
            type="number"
            min="0"
            max={maxAmount}
            value={requestedAmount}
            onChange={(e) => onAmountChange(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-2 py-1.5 font-mono"
          />
          <div className="text-xs text-text-tertiary mt-1">
            {t("wiz_loan.max", "Max")}: {maxAmount.toLocaleString()} SAR
          </div>
        </div>
        <div>
          <label className="block text-xs text-text-tertiary mb-1">
            {t("wiz_loan.tenure_months", "Tenure (months)")}
          </label>
          <input
            type="number"
            min="1"
            max={product.max_tenure_months}
            value={requestedTenure}
            onChange={(e) =>
              onTenureChange(Number.parseInt(e.target.value, 10) || 0)
            }
            className="w-full rounded-md border border-slate-300 px-2 py-1.5 font-mono"
          />
          <div className="text-xs text-text-tertiary mt-1">
            {t("wiz_loan.max", "Max")}: {product.max_tenure_months} mo
          </div>
        </div>
      </div>
      <Article90HeadroomWidget employeeId={employeeId} />
    </div>
  );
}

function DocsSection() {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">
        {t("wiz_loan.docs_title", "Supporting documents")}
      </h2>
      <p className="text-sm text-text-secondary">
        {t(
          "wiz_loan.docs_subtitle",
          "Some loan types (Hajj, Education) require evidence. Drag & drop file upload lands in a follow-up; for now, you can submit without and HR will request docs at approval.",
        )}
      </p>
      <div className="rounded-md border-2 border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-text-tertiary">
        {t(
          "wiz_loan.docs_dropzone",
          "Drop a PDF / DOC / JPG (max 10 MB) here — coming soon",
        )}
      </div>
    </div>
  );
}

function PurposeSection({
  purpose,
  productCode,
  onChange,
}: {
  purpose: string;
  productCode?: string;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  const handleAiAssist = () => {
    // PR23 wires the live LLM call. v1: deterministic stub.
    const stub: Record<string, string> = {
      HAJJ: "Funding for my once-in-lifetime Hajj pilgrimage. The amount covers travel, accommodation, and required offerings.",
      EDUCATION:
        "Funding for an executive education program that strengthens my professional skills and benefits the organization.",
      EMERGENCY:
        "Unexpected family medical expense that requires immediate funds.",
      PERSONAL:
        "Personal expenses including family obligations and household needs.",
    };
    const draft = productCode
      ? stub[productCode] ?? stub.PERSONAL
      : stub.PERSONAL;
    onChange(draft ?? "");
  };
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        {t("wiz_loan.purpose_title", "Describe the purpose")}
        <button
          onClick={handleAiAssist}
          className="text-xs px-2 py-1 rounded-md bg-ai-bg text-ai-deep border border-ai-accent/30 hover:border-ai-accent"
          aria-label={t(
            "wiz_loan.ai_assist_label",
            "Solvait AI assist — draft purpose",
          )}
        >
          ✦ {t("wiz_loan.ai_assist", "Solvait AI assist")}
        </button>
      </h2>
      <textarea
        value={purpose}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        placeholder={t(
          "wiz_loan.purpose_placeholder",
          "Describe what this loan is for. Khidmat can draft text for you with the assist button above.",
        )}
      />
    </div>
  );
}

function ReviewSection({
  draft,
  employeeId,
  product,
  canSubmit,
  submitting,
  onSubmit,
  submitError,
}: {
  draft: DraftState;
  employeeId: string;
  product: LoanProductDetail | undefined;
  canSubmit: boolean;
  submitting: boolean;
  onSubmit: () => void;
  submitError: string | null;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">
        {t("wiz_loan.review_title", "Review & submit")}
      </h2>
      <dl className="text-sm grid grid-cols-2 gap-y-2">
        <dt className="text-text-tertiary">{t("wiz_loan.type", "Type")}:</dt>
        <dd>{product?.name_en ?? "—"}</dd>
        <dt className="text-text-tertiary">
          {t("wiz_loan.amount", "Amount")}:
        </dt>
        <dd className="font-mono">{draft.requested_amount} SAR</dd>
        <dt className="text-text-tertiary">
          {t("wiz_loan.tenure", "Tenure")}:
        </dt>
        <dd className="font-mono">{draft.requested_tenure_months} mo</dd>
        <dt className="text-text-tertiary">
          {t("wiz_loan.purpose", "Purpose")}:
        </dt>
        <dd className="whitespace-pre-wrap">{draft.purpose || "—"}</dd>
      </dl>
      <Article90HeadroomWidget employeeId={employeeId} />
      {submitError ? (
        <ErrorPanel
          message={t("wiz_loan.submit_error", "Submission failed:") + " " + submitError}
        />
      ) : null}
      <button
        onClick={onSubmit}
        disabled={!canSubmit || submitting}
        className="w-full rounded-md bg-brand-blue text-white py-2 font-medium hover:bg-brand-deep disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting
          ? t("wiz_loan.submitting", "Submitting...")
          : t("wiz_loan.submit", "Submit loan request")}
      </button>
    </div>
  );
}
